/* @jsxImportSource react */

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { TilesValuesFragmentFragmentDoc } from '@packages/gql/generated/shopping/TilesValuesFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { getIsAvailable } from '../../helpers/getIsAvailable/getIsAvailable';
import { TileButton } from './TileButton/TileButton';
import { TileButtonDefault } from './TileButton/TileButtonDefault.client';
import type { VariationSelectionOnSelectHandler } from '../types';

/* GraphQL */ `
  fragment TilesValuesFragment on ProductDimension {
    values {
      displayText
      isSelected
      sku
      availability {
        state
      }
    }
  }
`;

export type TilesDimensionProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  data: FragmentType<typeof TilesValuesFragmentFragmentDoc>;
} & VariationSelectionOnSelectHandler;

/**
 * TilesDimension to choose a product variation on Detailview
 * */
export const TilesDimension = ({ data, onSelect }: TilesDimensionProps) => {
  const dimension = unmask<typeof TilesValuesFragmentFragmentDoc>(data);

  return (
    <div className="flex flex-wrap gap-1.5">
      {dimension.values.map(({ sku, displayText, availability, isSelected }, index) => {
        const isAvailable = getIsAvailable(availability);

        const key = sku || index;

        if (isSelected && isAvailable) {
          return (
            <TileButton key={key} mode="selected">
              {displayText}
            </TileButton>
          );
        }
        if (!isAvailable && !isSelected) {
          return (
            <TileButton key={key} mode="disabled">
              {displayText}
            </TileButton>
          );
        }
        if (!isAvailable && isSelected) {
          return (
            <TileButton key={key} mode="disabledAndSelected">
              {displayText}
            </TileButton>
          );
        }

        if (!sku) {
          return null;
        }
        return (
          <TileButtonDefault key={`${key}${sku}`} sku={sku} onSelect={onSelect}>
            {displayText}
          </TileButtonDefault>
        );
      })}
    </div>
  );
};
